import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { Job, JobStatus } from "@/lib/schema";
import { Search } from "lucide-react";
import { getJobs } from '@/api/jobs';
import { getJobSettings } from '@/api/jobSettings';
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { AvailableJobFilters } from "@/components/jobs/AvailableJobFilters";
import { AvailableJobCard } from "@/components/jobs/AvailableJobCard";
import { JobCardSkeletonGrid } from "@/components/ui/JobCardSkeleton";
import { 
  AvailableJobsFilters,
  hasActiveFilters as checkActiveFilters,
  filterJobsByFilters,
  extractUniqueLocations,
  createEmptyFilters
} from "@/lib/availableJobsUtils";

export default function AvailableJobs() {
  const { currentUser } = useAuth();
  const [_, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [filters, setFilters] = useState<AvailableJobsFilters>(createEmptyFilters());

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["/api/jobs", sortBy],
    queryFn: () => getJobs({ 
      sortBy,
      status: JobStatus.ACTIVE // Only show active jobs
    }),
  });

  // Fetch job settings for categories
  const { data: jobSettings } = useQuery({
    queryKey: ["/api/job-settings"],
    queryFn: getJobSettings,
  });

  // Generate unique locations from jobs data
  const availableLocations = jobs ? extractUniqueLocations(jobs) : [];
  const availableCategories = jobSettings?.categories || [];

  const handleReferSomeone = (job: Job) => {
    if (!currentUser) {
      // Handle unauthenticated user - could show auth modal
      return;
    }
    // Navigate to job details page for referral
    setLocation(`/jobs/${job.id}`);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setLocationFilter("all");
    setSortBy("recent");
    setFilters(createEmptyFilters());
  };

  // Check if any filters are active
  const hasFiltersActive = checkActiveFilters(searchQuery, locationFilter, filters);

  // Apply client-side filtering for search, location, and other filters
  const filteredJobs = jobs ? filterJobsByFilters(jobs, filters).filter(job => {
    // Search query filtering with regex-based keyword matching (company name excluded for privacy)
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const searchKeywords = searchQuery.trim().split(/\s+/);
      const searchableText = job.title.toLowerCase();
      
      // Check if any keyword matches using regex
      matchesSearch = searchKeywords.some(keyword => {
        try {
          const regex = new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
          return regex.test(searchableText);
        } catch {
          // Fallback to simple includes if regex fails
          return searchableText.includes(keyword.toLowerCase());
        }
      });
    }
    
    // Location filtering
    const matchesLocation = locationFilter === "all" || 
      job.location?.toLowerCase() === locationFilter.toLowerCase();
    
    return matchesSearch && matchesLocation;
  }) : [];

  return (
    <ProtectedRoute>
      <div className="min-h-screen page-gradient">
        <Sidebar />
        <div className="lg:ml-56 p-4 lg:p-8 pt-20 lg:pt-8">
          {/* Header */}
          <DashboardHeader 
            title="Available jobs" 
            subtitle="Browse open roles you can refer candidates to." 
          />

          {/* Search and Filters */}
          <AvailableJobFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            locationFilter={locationFilter}
            onLocationChange={setLocationFilter}
            filters={filters}
            onFiltersChange={setFilters}
            availableLocations={availableLocations}
            availableCategories={availableCategories}
            hasActiveFilters={hasFiltersActive}
            onClearFilters={handleClearFilters}
          />

          {/* Jobs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {isLoading ? (
              <JobCardSkeletonGrid count={6} />
            ) : filteredJobs.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2 space-grotesk">No jobs found</h3>
                <p className="text-gray-600 space-grotesk">Try adjusting your search criteria or filters.</p>
              </div>
            ) : (
              filteredJobs.map((job: Job, index: number) => (
                <AvailableJobCard
                  key={job.id}
                  job={job}
                  index={index}
                  onReferSomeone={handleReferSomeone}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}